/**
 * ============================================
 * AUTH TOKEN RECOVERY HELPER - iOS PWA Cookie Fix
 * ============================================
 *
 * Recovers authentication tokens from IndexedDB when cookies are missing.
 *
 * ## Problem Solved
 *
 * iOS PWA scope changes (e.g., /inspections → /) create new cookie storage:
 * - Cookies from old scope are inaccessible
 * - User appears "logged in" (localStorage has user data)
 * - But has no auth tokens (cookies missing)
 * - API calls fail with 401
 * - Offline mode shows "no internet" error
 *
 * ## Solution
 *
 * Dual-storage strategy:
 * 1. **Cookies** - Primary storage (existing functionality)
 * 2. **IndexedDB** - Backup storage (survives scope changes)
 *
 * When inspections app mounts:
 * 1. Check if cookies have tokens
 * 2. If missing, recover from IndexedDB
 * 3. Restore to cookies
 * 4. Continue normal operation
 *
 * ## Usage
 *
 * Call from inspections layout on mount (before sync engine starts):
 *
 * ```typescript
 * import { recoverAuthTokens } from '@/lib/inspections/auth-token-helper';
 *
 * useEffect(() => {
 *   const initializeSystem = async () => {
 *     // Recover tokens if cookies are missing
 *     const recovered = await recoverAuthTokens();
 *     if (recovered) {
 *       console.log('✅ Auth tokens recovered from IndexedDB');
 *     }
 *
 *     // Start sync engine
 *     await syncEngine.start();
 *   };
 *
 *   initializeSystem();
 * }, []);
 * ```
 *
 * ## Integration
 *
 * Works seamlessly with existing auth system:
 * - Tokens are dual-stored by auth store (see [auth.ts](../store/auth.ts))
 * - This helper only reads from IndexedDB, never writes
 * - Writes happen via normal auth flow (login, refresh)
 */

import { db } from "./db";
import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";

/**
 * Recover authentication tokens from IndexedDB
 *
 * Checks if cookies are missing auth tokens and attempts to recover
 * them from IndexedDB backup storage.
 *
 * This solves the iOS PWA cookie isolation issue when the app scope changes.
 *
 * @returns Promise<boolean> - true if tokens were recovered or already present, false if no tokens found
 *
 * @example
 * ```typescript
 * const hasTokens = await recoverAuthTokens();
 * if (hasTokens) {
 *   // Safe to start sync engine
 *   await syncEngine.start();
 * } else {
 *   // User needs to log in
 *   router.push('/auth/login');
 * }
 * ```
 */
export async function recoverAuthTokens(): Promise<boolean> {
  try {
    // Check if cookies already have tokens
    const existingAccessToken = Cookies.get("access_token");
    const existingRefreshToken = Cookies.get("refresh_token");

    if (existingAccessToken && existingRefreshToken) {
      return true; // Already logged in via cookies
    }

    // Try to recover from IndexedDB
    const [accessTokenRecord, refreshTokenRecord, tokenExpiresRecord] =
      await Promise.all([
        db.auth_tokens.get("access_token"),
        db.auth_tokens.get("refresh_token"),
        db.auth_tokens.get("token_expires"),
      ]);

    // Check if we have the required tokens in IndexedDB
    if (!accessTokenRecord || !refreshTokenRecord) {
      return false; // No tokens to recover
    }

    // Calculate expiration time
    const expires = tokenExpiresRecord
      ? parseInt(tokenExpiresRecord.value, 10)
      : Date.now() + 15 * 60 * 1000; // Default: 15 minutes from now

    // Restore tokens to cookies
    Cookies.set("access_token", accessTokenRecord.value, {
      expires: new Date(expires),
      path: "/",
    });

    Cookies.set("refresh_token", refreshTokenRecord.value, {
      expires: 7, // 7 days
      path: "/",
    });

    Cookies.set("token_expires", expires.toString(), {
      expires: new Date(expires),
      path: "/",
    });

    return true; // Tokens recovered successfully
  } catch (error) {
    console.error("[Auth Recovery] ❌ Failed to recover tokens:", error);
    Sentry.captureException(error, {
      tags: { area: "auth", stage: "token-recovery" },
    });
    return false; // Recovery failed
  }
}

/**
 * Check if auth tokens are available (either in cookies or IndexedDB)
 *
 * Useful for determining if user is authenticated without triggering recovery.
 *
 * @returns Promise<boolean> - true if tokens are available
 *
 * @example
 * ```typescript
 * if (await hasAuthTokens()) {
 *   // User has valid session
 * } else {
 *   // Redirect to login
 * }
 * ```
 */
export async function hasAuthTokens(): Promise<boolean> {
  // Check cookies first (fastest)
  if (Cookies.get("access_token") && Cookies.get("refresh_token")) {
    return true;
  }

  // Check IndexedDB
  try {
    const [accessToken, refreshToken] = await Promise.all([
      db.auth_tokens.get("access_token"),
      db.auth_tokens.get("refresh_token"),
    ]);

    return !!(accessToken && refreshToken);
  } catch (error) {
    console.error("[Auth Recovery] Failed to check IndexedDB tokens:", error);
    Sentry.captureException(error, {
      tags: { area: "auth", stage: "token-check" },
    });
    return false;
  }
}

/**
 * The access token this device last stored, expired or not, from the
 * IndexedDB backup the auth store writes on every login and refresh.
 * Undefined when there is none or IndexedDB cannot be read.
 */
export async function readLastKnownAccessToken(): Promise<string | undefined> {
  try {
    return (await db.auth_tokens.get("access_token"))?.value;
  } catch {
    return undefined;
  }
}
